import type { DevicePlatform } from "./devices";
import type {
  EncodingPreference,
  EphemeralTurnCredentials,
  MintSessionRequest,
  MintSessionResponse,
  MintTurnCredentialsRequest,
  MintTurnCredentialsResponse,
  SessionMode,
  SessionStatus,
} from "./sessions";
import type {
  PostSessionSignalRequest,
  PostSessionSignalResponse,
} from "./signaling";

export type {
  MintSessionRequest,
  MintSessionResponse,
  MintTurnCredentialsRequest,
  MintTurnCredentialsResponse,
  EphemeralTurnCredentials,
  SessionMode,
  SessionStatus,
  EncodingPreference,
  PostSessionSignalRequest,
  PostSessionSignalResponse,
};

export interface CreateEnrollmentTokenRequest {
  label?: string;
  groups?: string[];
  platform?: DevicePlatform;
  /** Override default TTL hours (capped server-side). */
  ttlHours?: number;
}

export interface CreateEnrollmentTokenResponse {
  tokenId: string;
  /** One-time plaintext secret — shown once to admin; never stored. */
  enrollmentSecret: string;
  expiresAt: string;
}

export interface EnrollDeviceRequest {
  tokenId: string;
  enrollmentSecret: string;
  displayName: string;
  platform: DevicePlatform;
  hostname?: string;
  /** Device-generated public key (private key stays in OS keystore). */
  publicKeyPem: string;
  algorithm: "Ed25519" | "ES256";
  agentVersion?: string;
}

export interface EnrollDeviceResponse {
  deviceId: string;
  /** Short-lived custom token for the agent to exchange for ID token. */
  customToken: string;
  tenantId: string;
}

export interface RevokeDeviceRequest {
  deviceId: string;
  reason?: string;
}

export interface RevokeDeviceResponse {
  deviceId: string;
  status: "revoked";
  revokedAt: string;
}

export interface RevokeEnrollmentTokenRequest {
  tokenId: string;
  reason?: string;
}

export interface RevokeEnrollmentTokenResponse {
  tokenId: string;
  status: "revoked";
}

export interface UpdateDevicePolicyRequest {
  deviceId: string;
  allowAttended?: boolean;
  allowUnattended?: boolean;
  /** Technician deviceGroups claims that may connect unattended. Empty disables group match. */
  unattendedAllowedGroups?: string[];
  requireStepUpMfa?: boolean;
  idleDisconnectSeconds?: number;
  maxSessionMinutes?: number;
  accessWindow?: {
    startUtc: string;
    endUtc: string;
  } | null;
}

export interface UpdateDevicePolicyResponse {
  deviceId: string;
  updatedAt: string;
}

export interface RotateDeviceCredentialsRequest {
  /** New device public key (private key stays in OS keystore). */
  publicKeyPem: string;
  algorithm: "Ed25519" | "ES256";
  /** Optional: retire this kid after rotation succeeds. */
  retireKid?: string;
}

export interface RotateDeviceCredentialsResponse {
  deviceId: string;
  kid: string;
  rotatedAt: string;
}

/**
 * Prove possession of enrolled device private key to mint a fresh custom token.
 * messageToSign = `${deviceId}:${nonce}:${timestamp}` (UTF-8); signature is base64url(raw Ed25519/ES256).
 */
export interface RefreshDeviceAuthRequest {
  deviceId: string;
  kid: string;
  nonce: string;
  /** Unix seconds — must be within ±5 minutes of server time. */
  timestamp: number;
  signatureBase64Url: string;
}

export interface RefreshDeviceAuthResponse {
  customToken: string;
  expiresHintSeconds: number;
}

/** Default agent heartbeat / revoke propagation window (seconds). */
export const AGENT_HEARTBEAT_INTERVAL_SECONDS = 30;

export interface EndSessionRequest {
  sessionId: string;
  reason?: string;
}

export interface EndSessionResponse {
  sessionId: string;
  status: SessionStatus;
  endedAt: string;
}

export interface AcceptSessionRequest {
  sessionId: string;
}

export interface AcceptSessionResponse {
  sessionId: string;
  status: SessionStatus;
  acceptedAt: string;
}

/** Agent validates technician session JWT received on the control DataChannel. */
export interface ValidateSessionJwtRequest {
  sessionId: string;
  sessionJwt: string;
}

export interface ValidateSessionJwtResponse {
  valid: boolean;
  deviceId?: string;
  technicianUid?: string;
  mode?: SessionMode;
  encodingPreference?: EncodingPreference;
  expiresAt?: string;
  reason?: string;
}

/** Callable function names (client SDK). */
export const CALLABLE_FUNCTIONS = {
  mintSession: "mintSession",
  mintTurnCredentials: "mintTurnCredentials",
  createEnrollmentToken: "createEnrollmentToken",
  enrollDevice: "enrollDevice",
  revokeDevice: "revokeDevice",
  revokeEnrollmentToken: "revokeEnrollmentToken",
  updateDevicePolicy: "updateDevicePolicy",
  rotateDeviceCredentials: "rotateDeviceCredentials",
  refreshDeviceAuth: "refreshDeviceAuth",
  endSession: "endSession",
  acceptSession: "acceptSession",
  rejectSession: "rejectSession",
  /** Admin-SDK write of SDP/ICE (Firestore client writes remain denied). */
  postSessionSignal: "postSessionSignal",
  /** Agent validates session JWT before injecting input. */
  validateSessionJwt: "validateSessionJwt",
  /** Phase 4 stub: persist recording consent decision. */
  requestRecordingConsent: "requestRecordingConsent",
  /** Phase 4 stub: ticketing session-link webhook (interface only). */
  notifyTicketingSession: "notifyTicketingSession",
} as const;

/** Optional ticketing fields accepted (and currently ignored for delivery) by mintSession. */
export type TicketingProvider = "zendesk" | "servicenow" | "generic";

export interface RequestRecordingConsentRequest {
  sessionId: string;
  /** Customer/agent grant or deny; technicians may only request via mintSession. */
  granted: boolean;
  source: "customer" | "policy";
}

export interface RequestRecordingConsentResponse {
  sessionId: string;
  recordingConsentGranted: boolean;
  recordingConsentAt: string;
}

export interface NotifyTicketingSessionRequest {
  sessionId: string;
  ticketId: string;
  provider: TicketingProvider;
  event: "session.minted" | "session.ended" | "session.accepted";
}

export interface NotifyTicketingSessionResponse {
  accepted: boolean;
  /** Stub always returns delivered=false until adapters ship. */
  delivered: boolean;
  detail: string;
}
